import api from "./api";
import { ok, getLookupsCached, listCyclesCached, isoUtc, nameOf, downloadFrom } from "./core";

export const REPORT_TYPES = [
  { key: "employee-performance", label: "Employee performance", aggregate: false },
  { key: "completion", label: "Cycle completion", aggregate: false },
  { key: "pending-actions", label: "Pending actions", aggregate: false },
  { key: "department-performance", label: "Department performance", aggregate: true },
  { key: "rating-distribution", label: "Rating distribution", aggregate: true },
  { key: "trend", label: "Cycle trend", aggregate: true },
];

const LABELS = {
  employee_id: "Employee ID", employee_name: "Employee", department_id: "Department", department_name: "Department",
  designation_name: "Designation", cycle_id: "Cycle", goal_score: "Goal score", kra_score: "KRA score", kpi_score: "KPI score",
  final_score: "Final score", rating: "Rating", status: "Status", stage: "Stage", eligibility: "Eligibility",
  manager_id: "Manager", owner: "Pending with", due_date: "Due", count: "Employees", percentage: "Share %",
  eligible_count: "Eligible", released_count: "Released", average_score: "Average score",
};
const pretty = (v) => (typeof v === "string" ? v.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase()) : v);

export async function getReport(cycleId, type, params = {}) {
  const [lookups, cycles, { data }] = await Promise.all([
    getLookupsCached(),
    listCyclesCached(),
    api.get(`/reports/${type}`, { params: { cycle_id: type === "trend" ? undefined : cycleId, department_id: params.departmentId || undefined, status: params.status, limit: 500 } }),
  ]);
  // [UPDATED] Requirement 3: the KPI score column is not shown in reports.
  const columns = data.columns.filter((c) => c !== "kpi_score" && !(c === "department_id" && data.columns.includes("department_name")));
  const rows = data.rows.map((r) => {
    const out = { ...r };
    if ("department_id" in r) out.department_id = lookups.departments.find((d) => d.id === String(r.department_id))?.name || r.department_id || "—";
    if ("cycle_id" in r) out.cycle_id = cycles.find((c) => c.cycleId === r.cycle_id)?.name || r.cycle_id;
    if ("manager_id" in r) out.manager_id = nameOf(r.manager_id);
    if ("owner" in r) out.owner = nameOf(r.owner);
    if ("due_date" in r) out.due_date = r.due_date ? new Date(isoUtc(r.due_date)).toLocaleDateString("en-IN") : "—";
    ["status", "stage", "eligibility"].forEach((k) => { if (k in r) out[k] = pretty(r[k]); });
    ["goal_score", "kra_score", "kpi_score", "final_score", "average_score"].forEach((k) => {
      if (k in r && r[k] !== null) out[k] = Math.round(Number(r[k]) * 100) / 100;
    });
    return out;
  });
  return ok({ columns: columns.map((key) => ({ key, label: LABELS[key] || pretty(key) })), rows, total: data.total });
}

export async function exportReport(cycleId, type, params = {}) {
  const { data } = await api.post(`/reports/${type}/exports`, null, {
    params: { cycle_id: type === "trend" ? undefined : cycleId, department_id: params.departmentId || undefined, status: params.status },
  });
  const name = await downloadFrom(`/report-exports/${data.report_export_id}/download`, `${type}-${cycleId}.xlsx`);
  return ok({ ...data, fileName: name });
}

// [NEW] "Where we're heading" (Reports): two answers worked out from the existing reports, no backend change.
// 1. Performance direction: average released score of the latest cycle with results vs the cycle before (trend report).
// 2. Progress pace: for the stage window open today, the share of appraisals already past it vs the share of the
//    window's time used; plus appraisals still stuck in a stage whose window has closed (overdue).
const STAGE_RANK = { GOAL_SETTING: 0, GOAL_APPROVAL: 1, TRACKING: 2, SELF_ASSESSMENT: 3, MANAGER_ASSESSMENT: 4, HR_FINALIZATION: 5 };
const DONE_STATUSES = ["FINALIZED", "RELEASED", "ACKNOWLEDGED", "CLOSED"];
// Rank an appraisal must reach to have finished each window.
const WINDOW_DONE = { goalSetting: 2, selfAssessment: 4, managerReview: 5, hrFinalization: 6 };
const WINDOW_LABEL = { goalSetting: "Goal setting", selfAssessment: "Self assessment", managerReview: "Manager review", hrFinalization: "HR finalisation" };
const rankOf = (r) => (DONE_STATUSES.includes(r.status) ? 6 : STAGE_RANK[r.stage] ?? 0);
const dayMs = 24 * 60 * 60 * 1000;

export async function getHeading(cycle) {
  const out = { performance: null, progress: null };
  const [trend, completion] = await Promise.all([
    api.get("/reports/trend", { params: { limit: 500 } }).then((r) => r.data).catch(() => null),
    cycle ? api.get("/reports/completion", { params: { cycle_id: cycle.cycleId, limit: 500 } }).then((r) => r.data).catch(() => null) : null,
  ]);

  if (trend) {
    const cycles = await listCyclesCached();
    const scored = trend.rows
      .map((r) => ({ cycle: cycles.find((c) => c.cycleId === r.cycle_id), score: r.average_score === null ? null : Number(r.average_score) }))
      .filter((r) => r.cycle && r.score !== null)
      .sort((a, b) => String(a.cycle.startDate).localeCompare(String(b.cycle.startDate)));
    const last = scored[scored.length - 1];
    const prev = scored[scored.length - 2];
    out.performance = {
      series: scored.map((r) => ({ name: r.cycle.name, value: Math.round(r.score * 100) / 100 })),
      latest: last ? { name: last.cycle.name, score: last.score } : null,
      previous: prev ? { name: prev.cycle.name, score: prev.score } : null,
      change: last && prev ? Math.round((last.score - prev.score) * 100) / 100 : null,
    };
  }

  if (completion && cycle) {
    const rows = completion.rows.filter((r) => r.eligibility === "ELIGIBLE");
    const now = Date.now();
    const windows = (cycle.stages || []).filter((s) => WINDOW_DONE[s.key] !== undefined && s.startDate && s.endDate)
      .map((s) => ({ ...s, start: new Date(`${s.startDate}T00:00:00`).getTime(), end: new Date(`${s.endDate}T23:59:59`).getTime() }));
    const open = windows.find((w) => now >= w.start && now <= w.end) || null;
    const closed = windows.filter((w) => now > w.end);
    const mustReach = closed.length ? Math.max(...closed.map((w) => WINDOW_DONE[w.key])) : 0;
    const overdue = rows.filter((r) => rankOf(r) < mustReach).length;
    let pace = null;
    if (open && rows.length) {
      const done = rows.filter((r) => rankOf(r) >= WINDOW_DONE[open.key]).length;
      const donePct = Math.round((done / rows.length) * 100);
      const timePct = Math.min(100, Math.max(0, Math.round(((now - open.start) / (open.end - open.start)) * 100)));
      pace = {
        stage: WINDOW_LABEL[open.key], done, total: rows.length, donePct, timePct,
        daysLeft: Math.max(0, Math.ceil((open.end - now) / dayMs)),
        verdict: donePct >= timePct - 10 ? "on-track" : donePct >= timePct - 30 ? "at-risk" : "behind",
      };
    }
    out.progress = { total: rows.length, overdue, mustReachLabel: closed.length ? WINDOW_LABEL[closed[closed.length - 1].key] : null, pace };
  }
  return ok(out);
}
